import { useMemo } from 'react'
import { useFolder } from './useFolders'
import type { DocumentCategory, CategorySuggestion, DocumentFolder } from '../types'

/**
 * Détecte une catégorie depuis un nom de fichier côté client
 * (Réplique la logique SQL pour l'UX immédiate)
 */
function detectCategoryFromFilename(filename: string): DocumentCategory | null {
  const filenameLower = filename.toLowerCase()

  // Assemblées générales
  if (/(^|[^a-z])(pv|proces[- ]verbal|convocation|ag|assemblee|resolution|feuille[- ]presence)([^a-z]|$)/.test(filenameLower)) {
    return 'ag'
  }

  // Documents constitutifs
  if (/(^|[^a-z])(reglement|copropriete|etat[- ]descriptif|division|acte[- ]fondateur|modificatif)([^a-z]|$)/.test(filenameLower)) {
    return 'constitutif'
  }

  // Comptabilité
  if (/(^|[^a-z])(budget|compte|comptable|appel[- ]fonds|situation[- ]comptable|rapprochement[- ]bancaire)([^a-z]|$)/.test(filenameLower)) {
    return 'comptabilite'
  }

  // Travaux
  if (/(^|[^a-z])(travaux|devis|facture|chantier|pv[- ]reception)([^a-z]|$)/.test(filenameLower)) {
    return 'travaux'
  }

  // Technique/Diagnostics
  if (/(^|[^a-z])(diagnostic|dpe|amiante|plomb|electricite|gaz|rapport[- ]visite|controle[- ]reglementaire|plan)([^a-z]|$)/.test(filenameLower)) {
    return 'technique'
  }

  // Sinistres
  if (/(^|[^a-z])(sinistre|degat[- ]eaux|assurance|expertise|declaration)([^a-z]|$)/.test(filenameLower)) {
    return 'sinistres'
  }

  // Juridique
  if (/(^|[^a-z])(juridique|contentieux|tribunal|jugement|mise[- ]demeure|avocat)([^a-z]|$)/.test(filenameLower)) {
    return 'juridique'
  }

  // Contrats
  if (/(^|[^a-z])(contrat|prestataire|fournisseur)([^a-z]|$)/.test(filenameLower)) {
    return 'contrats'
  }

  // Correspondance
  if (/(^|[^a-z])(courrier|email|correspondance|lettre)([^a-z]|$)/.test(filenameLower)) {
    return 'correspondance'
  }

  // Copropriétaires
  if (/(^|[^a-z])(piece[- ]identite|acte[- ]propriete|rib|mandat[- ]sepa|justificatif)([^a-z]|$)/.test(filenameLower)) {
    return 'coproprietaire'
  }

  return null
}

/**
 * Remonte l'arborescence des dossiers pour trouver une suggested_category
 */
function getSuggestedCategoryFromFolderHierarchy(
  folderId: string | undefined,
  foldersMap: Map<string, any>
): DocumentCategory | null {
  if (!folderId) return null

  let currentFolderId: string | undefined = folderId
  let depth = 0
  const maxDepth = 10 // Protection contre boucles infinies

  while (currentFolderId && depth < maxDepth) {
    const folder = foldersMap.get(currentFolderId)
    if (!folder) break

    // Si on trouve une suggested_category, on la retourne
    if (folder.suggested_category) {
      return folder.suggested_category
    }

    // Sinon on remonte au parent
    currentFolderId = folder.parent_folder_id
    depth++
  }

  return null
}

interface UseCategorySuggestionParams {
  folderId?: string
  filename?: string
  folders?: DocumentFolder[] // Liste de tous les dossiers de la résidence
}

/**
 * Hook pour suggérer automatiquement une catégorie basée sur le dossier et le nom de fichier
 */
export function useCategorySuggestion({
  folderId,
  filename,
  folders = [],
}: UseCategorySuggestionParams): CategorySuggestion {
  // Créer une map des dossiers pour recherche rapide
  const foldersMap = useMemo(() => {
    const map = new Map()
    folders.forEach((folder) => {
      map.set(folder.id, folder)
    })
    return map
  }, [folders])

  const suggestion = useMemo((): CategorySuggestion => {
    // Priorité 1: Suggestion depuis le dossier (remonte l'arborescence)
    const folderSuggestion = getSuggestedCategoryFromFolderHierarchy(folderId, foldersMap)

    if (folderSuggestion) {
      const folder = folderId ? foldersMap.get(folderId) : null
      return {
        category: folderSuggestion,
        confidence: 'high',
        source: 'folder',
        message: folder ? `Catégorie suggérée depuis le dossier "${folder.name}"` : undefined,
      }
    }

    // Priorité 2: Détection depuis le nom de fichier
    if (filename) {
      const filenameSuggestion = detectCategoryFromFilename(filename)
      if (filenameSuggestion) {
        return {
          category: filenameSuggestion,
          confidence: 'medium',
          source: 'filename',
          message: `Catégorie détectée depuis le nom du fichier`,
        }
      }
    }

    // Aucune suggestion
    return {
      category: null,
      confidence: 'low',
      source: 'none',
    }
  }, [folderId, filename, foldersMap])

  return suggestion
}

/**
 * Hook simplifié pour charger les informations d'un dossier et obtenir sa suggested_category
 */
export function useFolderSuggestion(folderId?: string) {
  const { data: folder, isLoading } = useFolder(folderId)

  return {
    suggestedCategory: folder?.suggested_category || null,
    folder,
    isLoading,
  }
}
